import { useEffect, useRef, useCallback } from "react";

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (error: string) => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: (error: string) => void;
          theme?: "light" | "dark" | "auto";
          language?: string;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

export function TurnstileWidget({ onVerify, onExpire, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const scriptLoadedRef = useRef(false);

  // Detectar ambiente de desenvolvimento/preview
  const isDev = import.meta.env.DEV || 
                window.location.hostname === "localhost" ||
                window.location.hostname.includes("lovable");

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;

    if (!TURNSTILE_SITE_KEY) {
      console.warn("[Turnstile] VITE_TURNSTILE_SITE_KEY não configurada");
      // Em desenvolvimento/preview sem chave, simular verificação
      if (isDev) {
        console.log("[Turnstile] Modo desenvolvimento/preview - simulando verificação");
        setTimeout(() => onVerify("dev-token-simulated"), 1000);
      }
      return;
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: onVerify,
        "expired-callback": onExpire,
        "error-callback": onError,
        theme: "auto",
        language: "pt-br",
      });
    } catch (error) {
      console.error("[Turnstile] Erro ao renderizar widget:", error);
    }
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    // Verificar se script já está carregado
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Evitar carregar script múltiplas vezes
    if (scriptLoadedRef.current) return;
    scriptLoadedRef.current = true;

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log("[Turnstile] Script carregado com sucesso");
      // Pequeno delay para garantir que turnstile está disponível
      setTimeout(renderWidget, 100);
    };
    
    script.onerror = () => {
      console.error("[Turnstile] Falha ao carregar script");
      onError?.("Falha ao carregar CAPTCHA");
    };

    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignorar erros de remoção
        }
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget, onError]);

  // Detectar ambiente de desenvolvimento/preview para renderização
  const isDevEnv = import.meta.env.DEV || 
                   window.location.hostname === "localhost" ||
                   window.location.hostname.includes("lovable");

  if (!TURNSTILE_SITE_KEY && !isDevEnv) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground border rounded-lg">
        CAPTCHA não configurado
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="flex justify-center"
      data-testid="turnstile-container"
    />
  );
}
